import api from "api";
import { computed, makeAutoObservable, toJS } from "mobx";
import { RootStore } from "store";
import { ICartItem, PaymentMethod } from "types/cart";
import { v4 as uuidv4 } from "uuid";
import { makePersistable } from "mobx-persist-store";
import { MembershipType, IUser } from "types/user";
import { roundToNearestHalf } from "utils/numberHelpers";

export const GUEST_MARKUP = 1.1;

const discountMapper: { [membershipType in MembershipType]: number } = {
	Staff: 0.5,
	// staff price is 0.5 discount
	Coffee: 1,
	Guest: GUEST_MARKUP,
	// guest price is 1.1 markup
	VIP: 1,
};

class CartStore {
	items: ICartItem[] = [];
	expandedItem: ICartItem | null = null;
	rootStore: RootStore;
	orderingUser: IUser | null = null;
	isGuestOrder: boolean = true;
	discountRate: number | undefined = undefined;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		makePersistable(this, { name: "genius-order:cart", properties: ["items"], storage: window.localStorage });
		makeAutoObservable(this);
		this.setDiscountRate(+((1 - this.defaultDiscount) * 100).toFixed(2));
	}

	@computed get defaultDiscount() {
		if (this.isGuestOrder) return GUEST_MARKUP;
		if (this.orderingUser === null) return 1;
		return discountMapper[this.orderingUser.membershipType];
	}

	resetDiscount = () => {
		this.setDiscountRate(+((1 - this.defaultDiscount) * 100).toFixed(2));
	};

	@computed get priceMultiplier() {
		if (this.discountRate !== undefined) return 1 - this.discountRate / 100;
		return this.defaultDiscount;
	}

	@computed get discountAvailable() {
		return !(this.orderingUser === null || this.isGuestOrder || this.orderingUser.membershipType === "Guest");
	}

	@computed get isStaff() {
		return this.orderingUser && this.orderingUser.membershipType === "Staff";
	}

	@computed get totalPrice() {
		return roundToNearestHalf(
			this.priceMultiplier *
				this.items.reduce((prevTotalPrice, currItem) => {
					const optionsPrice = Object.values(currItem.options || []).reduce((prev, curr) => curr.price + prev, 0);
					return prevTotalPrice + (currItem.price + optionsPrice) * currItem.quantity;
				}, 0)
		);
	}

	setDiscountRate = (discount: number) => {
		this.discountRate = discount;
	};

	setExpandedItem = (cartItem: ICartItem) => {
		this.expandedItem = cartItem;
	};

	removeExpandedItem = () => (this.expandedItem = null);

	setIsGuestOrder = (boolValue: boolean) => {
		if (boolValue) this.orderingUser = null;
		this.isGuestOrder = boolValue;
		this.resetDiscount();
	};

	setOrderingUser = (user: IUser) => {
		this.orderingUser = user;
		this.resetDiscount();
	};

	add = (item: ICartItem) => {
		return (this.items = [...this.items, { ...item, id: uuidv4() }]);
	};

	remove = (idToRemove: string) => {
		return (this.items = this.items.filter((cartItem) => cartItem.id !== idToRemove));
	};

	update = (item: ICartItem) => {
		const indexToUpdate = this.items.findIndex((cartItem) => cartItem.id === item.id);
		return (this.items[indexToUpdate] = item);
	};

	clear = () => {
		this.items = [];
	};

	submitTransaction = (paymentMethod: PaymentMethod, signatureImg?: string) => {
		if (!this.items.length) return Promise.reject(Error("Empty cart"));
		if (this.isGuestOrder) return api.transaction.guestCreate(this.items, paymentMethod, this.discountRate).then(this.clear);
		if (!this.orderingUser) throw Promise.reject(Error("Select a user"));
		return api.transaction.createForUser(this.orderingUser.id, this.items, signatureImg!, paymentMethod, this.discountRate).then(this.clear);

		// return api.transaction.create(this.items, paymentMethod).then(this.clear);
	};
}

export default CartStore;
